
/* POOL24 PREMIUM HOME v3.0 — mobile-first, accessible, dependency-free */
(() => {
  'use strict';

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const smooth = () => reduceMotion() ? 'auto' : 'smooth';
  const format = (n) => String(n).padStart(2, '0');

  function setup(root) {
    if (!root || root.dataset.p24Ready === 'true') return;
    root.dataset.p24Ready = 'true';
    const cleaners = [];
    const listen = (target, name, handler, options) => {
      target.addEventListener(name, handler, options);
      cleaners.push(() => target.removeEventListener(name, handler, options));
    };

    // HERO: slides, accessible controls, swipe, autoplay, editor preview.
    const hero = root.querySelector('[data-p24-hero]');
    let showHero = () => {};
    if (hero) {
      const slides = [...hero.querySelectorAll('[data-p24-hero-slide]')];
      const dots = [...hero.querySelectorAll('[data-p24-hero-dot]')];
      const count = hero.querySelector('[data-p24-hero-count]');
      let active = 0;
      let timer = null;
      let visible = true;
      let touchX = null;
      let touchY = null;
      const interval = Math.max(5000, Number(root.dataset.interval) || 7000);
      const auto = root.dataset.autoplay === 'true';
      const pause = () => { if (timer !== null) clearInterval(timer); timer = null; };
      const play = () => {
        pause();
        if (!auto || slides.length < 2 || reduceMotion() || !visible || document.hidden || hero.matches(':hover') || hero.contains(document.activeElement) || root.querySelector('.p24-dialog[open]')) return;
        timer = window.setInterval(() => showHero(active + 1), interval);
      };
      showHero = (n) => {
        if (!slides.length) return;
        active = ((n % slides.length) + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          const selected = i === active;
          slide.classList.toggle('is-active', selected);
          slide.setAttribute('aria-hidden', selected ? 'false' : 'true');
          if (selected) slide.removeAttribute('inert');
          else slide.setAttribute('inert', '');
        });
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === active);
          if (i === active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        if (count) count.textContent = `${format(active + 1)} / ${format(slides.length)}`;
      };
      const go = (delta) => { showHero(active + delta); play(); };
      const previous = hero.querySelector('[data-p24-hero-prev]');
      const next = hero.querySelector('[data-p24-hero-next]');
      if (previous) listen(previous, 'click', () => go(-1));
      if (next) listen(next, 'click', () => go(1));
      dots.forEach((dot, i) => listen(dot, 'click', () => { showHero(i); play(); }));
      listen(hero, 'mouseenter', pause);
      listen(hero, 'mouseleave', play);
      listen(hero, 'focusin', pause);
      listen(hero, 'focusout', () => window.requestAnimationFrame(play));
      listen(hero, 'touchstart', (e) => {
        touchX = e.touches[0]?.clientX ?? null;
        touchY = e.touches[0]?.clientY ?? null;
        pause();
      }, { passive: true });
      listen(hero, 'touchend', (e) => {
        if (touchX === null || touchY === null) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchX) - touchX;
        const dy = (e.changedTouches[0]?.clientY ?? touchY) - touchY;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) showHero(active + (dx < 0 ? 1 : -1));
        touchX = touchY = null;
        play();
      }, { passive: true });
      listen(document, 'visibilitychange', play);
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting;
          if (visible) play(); else pause();
        }, { threshold: 0.15 });
        observer.observe(hero);
        cleaners.push(() => observer.disconnect());
      }
      cleaners.push(pause);
      showHero(0);
      play();
    }

    // Horizontal rails: native touch scrolling + arrow buttons + progress.
    root.querySelectorAll('[data-p24-carousel]').forEach((rail) => {
      const key = rail.dataset.p24Carousel;
      const controls = root.querySelector(`[data-p24-controls="${key}"]`);
      const previous = controls?.querySelector('[data-p24-prev]');
      const next = controls?.querySelector('[data-p24-next]');
      const progress = root.querySelector(`[data-p24-progress="${key}"]`);
      const counter = root.querySelector(`[data-p24-counter="${key}"]`);
      const cards = [...rail.children];
      let frame = 0;
      const step = () => {
        if (!cards.length) return rail.clientWidth;
        return cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).gap) || 0);
      };
      const update = () => {
        const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const fraction = max ? Math.max(0, Math.min(1, rail.scrollLeft / max)) : 1;
        if (progress) progress.style.width = `${max ? 12 + fraction * 88 : 100}%`;
        const current = Math.min(cards.length, Math.max(1, Math.round(rail.scrollLeft / Math.max(1, step())) + 1));
        if (counter) counter.textContent = `${format(current)} / ${format(cards.length)}`;
        if (previous) previous.disabled = rail.scrollLeft < 3;
        if (next) next.disabled = rail.scrollLeft >= max - 3;
      };
      const move = (direction) => rail.scrollBy({ left: direction * step(), behavior: smooth() });
      if (previous) listen(previous, 'click', () => move(-1));
      if (next) listen(next, 'click', () => move(1));
      listen(rail, 'scroll', () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => { frame = 0; update(); });
      }, { passive: true });
      listen(rail, 'keydown', (event) => {
        if (event.target !== rail) return;
        if (event.key === 'ArrowRight') { event.preventDefault(); move(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
      });
      listen(window, 'resize', update, { passive: true });
      cleaners.push(() => { if (frame) window.cancelAnimationFrame(frame); });
      update();
    });

    // Shopify product-gallery thumbnails: honour selected image on mobile too.
    root.querySelectorAll('[data-p24-thumb]').forEach((thumb) => {
      listen(thumb, 'click', () => {
        const card = thumb.closest('.p24-model');
        const img = card?.querySelector('.p24-model__image');
        const src = thumb.dataset.src;
        if (!img || !src) return;
        img.src = src;
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        card.querySelectorAll('picture source').forEach((source) => source.remove());
        img.alt = thumb.dataset.alt || img.alt;
        card.querySelectorAll('[data-p24-thumb]').forEach((item) => {
          item.classList.toggle('is-active', item === thumb);
          item.setAttribute('aria-pressed', item === thumb ? 'true' : 'false');
        });
      });
    });

    // Full SEO texts are already server-rendered inside native dialogs.
    root.querySelectorAll('[data-p24-open]').forEach((button) => {
      listen(button, 'click', () => {
        let token = button.dataset.p24Open;
        if (token === 'faq-all') token = root.querySelector('[data-p24-faq-target]')?.dataset.p24FaqTarget || 'archive';
        const dialog = document.getElementById(`${root.id}-${token}`);
        if (!dialog || !root.contains(dialog)) return;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        dialog.querySelector('.p24-dialog__scroll')?.scrollTo({ top: 0 });
        // Pause background hero while reading.
        hero?.dispatchEvent(new Event('mouseenter'));
      });
    });
    root.querySelectorAll('.p24-dialog').forEach((dialog) => {
      const close = () => {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      };
      dialog.querySelectorAll('[data-p24-close]').forEach((button) => listen(button, 'click', close));
      listen(dialog, 'click', (e) => { if (e.target === dialog) close(); });
      listen(dialog, 'close', () => hero?.dispatchEvent(new Event('mouseleave')));
    });

    // In Shopify Theme Editor, selecting a block reveals its carousel card.
    const onBlockSelect = (event) => {
      const selected = event.target.closest?.('[data-p24-block], [data-shopify-editor-block]') || event.target;
      if (!root.contains(selected)) return;
      const slide = selected.closest('[data-p24-hero-slide]');
      if (slide && hero) {
        const slides = [...hero.querySelectorAll('[data-p24-hero-slide]')];
        showHero(slides.indexOf(slide));
        hero.dispatchEvent(new Event('mouseenter'));
      }
      const rail = selected.closest('[data-p24-carousel]');
      if (rail) selected.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
    };
    listen(document, 'shopify:block:select', onBlockSelect);

    root.__p24Cleanup = () => {
      cleaners.forEach((clean) => clean());
      root.dataset.p24Ready = 'false';
    };
  }

  function boot(scope = document) {
    if (scope.matches?.('[data-p24-home]')) setup(scope);
    scope.querySelectorAll?.('[data-p24-home]').forEach(setup);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = event.target.matches?.('[data-p24-home]') ? [event.target] : [...event.target.querySelectorAll('[data-p24-home]')];
    roots.forEach((root) => root.__p24Cleanup?.());
  });
})();
